import React, { useState, useRef, useCallback } from 'react';
import { BringzzIcon, BringzzText, BringzzProgressBar } from '@bringzz/components';
import { useAuth } from 'context/AuthContext';
import classNames from 'classnames';
import { useNavigation } from 'context/NavigationContext';
import { useOrder } from 'context/OrderContext';
import { customerPickupSegments, deliverySegments, pharmacyPickupSegments, pharmacyCollectSegments, customerCollectSegments, pharmacyMailSegments, customerMailSegments } from 'constants/orderSegments';
import { USER_ROLES, DELIVERY_TYPES } from 'utils/constants';

const SWIPE_THRESHOLD = 50;

// mode: 'micro' | 'minimized' | 'full'
const ProgressOverlay = () => {
    const { activeStep, getCurrentPage, orderDetails } = useOrder();
    const { navigate, isActive } = useNavigation();
    const [mode, setMode] = useState('micro');
    const { user } = useAuth();
    const touchStartY = useRef(null);

    const handleTouchStart = useCallback((e) => {
        touchStartY.current = e.touches[0].clientY;
    }, []);

    const handleTouchEndFull = useCallback((e) => {
        if (touchStartY.current === null) return;
        const deltaY = e.changedTouches[0].clientY - touchStartY.current;
        touchStartY.current = null;
        if (deltaY > SWIPE_THRESHOLD) {
            setMode('micro');
        }
    }, []);

    const handleTouchEndMicro = useCallback((e) => {
        if (touchStartY.current === null) return;
        const deltaY = e.changedTouches[0].clientY - touchStartY.current;
        touchStartY.current = null;
        if (deltaY < -SWIPE_THRESHOLD) {
            setMode('minimized');
        }
    }, []);

    const handleMinimize = () => {
        setMode('minimized');
    };

    const handleMaximize = () => {
        setMode('full');
    };

    const handleMicro = () => {
        setMode('micro');
    };

    const handleViewDetails = () => {
        const nextStep = getCurrentPage(activeStep);
        navigate(nextStep);
    };

    let steps;
    console.log("user role", user.userRole);
    if (user.userRole === USER_ROLES.PHARMACY_ROLE) {
        if (orderDetails.type == DELIVERY_TYPES.BRINGZZ_PICKUP) {
            steps = pharmacyPickupSegments(navigate);
        } else if (orderDetails.type == DELIVERY_TYPES.CLICK_AND_COLLECT) {
            steps = pharmacyCollectSegments(navigate);
        } else if (orderDetails.type == DELIVERY_TYPES.MAIL_DELIVERY) {
            steps = pharmacyMailSegments(navigate);
        }
    } else if (user.userRole === USER_ROLES.CONSUMER_ROLE) {
        if (orderDetails.type == DELIVERY_TYPES.BRINGZZ_PICKUP) {
            steps = customerPickupSegments(navigate);
        } else if (orderDetails.type == DELIVERY_TYPES.CLICK_AND_COLLECT) {
            steps = customerCollectSegments(navigate);
        } else if (orderDetails.type == DELIVERY_TYPES.MAIL_DELIVERY) {
            steps = customerMailSegments(navigate);
        }
    } else if (user.userRole === USER_ROLES.SHOPPER_ROLE) {
        steps = deliverySegments(navigate);
    }

    console.log("steps", steps);

    // If orderDetails is null, or one of the pages is active, don't render the component
    if (!orderDetails || isActive('/instruction') || isActive('/qrcode') || isActive('/matchmaking') || isActive('/navigation') || isActive('/prescription')) return null;

    if (!Array.isArray(steps) || typeof activeStep !== 'number' || activeStep < 0 || activeStep >= steps.length) {
        return null;
    }

    if (mode === 'micro') {
        return (
            <div
                className={classNames(
                    'fixed right-12 z-50 bottom-14'
                )}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEndMicro}
                onClick={handleMinimize}
            >
                <div className="w-12 h-12 bg-magic-lilac rounded-full cursor-pointer flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.25)]">
                    <img src="/favicon.png" alt="Bringzz" className="w-7 h-7 object-contain brightness-0 invert" />
                </div>
            </div>
        );
    }

    if (mode === 'minimized') {
        return (
            <div
                className={classNames(
                    'fixed right-4 z-50 shadow-lg bottom-20',
                )}
            >
                <div
                    className="bg-magic-lilac text-white p-4 rounded-lg cursor-pointer shadow-lg w-[75vw] max-w-[300px] relative"
                    onClick={handleMaximize}
                >
                    <BringzzIcon
                        size="12"
                        icon="IconClose"
                        folder="LineIcons"
                        className="cursor-pointer text-white absolute top-3 right-3"
                        onClick={(e) => { e.stopPropagation(); handleMicro(); }}
                    />
                    <div className="flex items-center justify-between mb-2 pr-5">
                        <BringzzText tag="h5" className="font-normal text-white truncate">
                            Order #{orderDetails.id}
                        </BringzzText>
                        <BringzzIcon icon="IconExpand" size="16" folder="LineIcons" className="text-white flex-shrink-0" />
                    </div>
                    <BringzzText tag="h6" className="text-white font-medium truncate">
                        {steps[activeStep].status}
                    </BringzzText>
                    <div className="flex items-center mt-1 space-x-1">
                        <div className="h-1 bg-white rounded-full flex-grow">
                            <div
                                className="h-full bg-primary rounded-full"
                                style={{ width: `${((activeStep + 1) / steps.length) * 100}%` }}
                            />
                        </div>
                        <BringzzText tag="span" className="text-white text-xs">
                            {activeStep + 1}/{steps.length}
                        </BringzzText>
                    </div>
                </div>
            </div>
        );
    }

    // mode === 'full'
    return (
        <div
            className='fixed w-full max-w-xl p-6 z-50 flex justify-center items-center bottom-16 md:bottom-0'
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEndFull}
        >
            <div className="bg-magic-lilac w-full max-w-xl rounded-lg shadow-lg relative">
                <BringzzIcon
                    size="16"
                    icon="IconClose"
                    folder="LineIcons"
                    className="cursor-pointer text-white absolute top-4 right-4"
                    onClick={handleMinimize}
                />

                <div className="p-4">
                    <BringzzText tag="h4" className="font-normal text-white truncate">
                        Order #{orderDetails.id}
                    </BringzzText>
                    <BringzzText tag="h3" className="text-white truncate">
                        {steps[activeStep].status}
                    </BringzzText>
                    <BringzzText tag="h5" className="font-light mb-3 text-white truncate">
                        {steps[activeStep].subStatus}
                    </BringzzText>
                    <BringzzProgressBar state={Number(activeStep) + 1} mode="custom" segments={steps} />
                </div>
                <hr />
                <div
                    className="flex justify-center py-3 items-center space-x-2 cursor-pointer"
                    onClick={handleViewDetails}
                >
                    <BringzzText tag="h4" className="text-white font-normal">
                        View details
                    </BringzzText>
                    <BringzzIcon icon="IconChevronDown" folder="LineIcons" size="12" className="text-white" />
                </div>
            </div>
        </div>
    );
};

export default ProgressOverlay;
